import {
  type InternalNoteDto,
  type ListInternalNotesOutput,
  claimInternalNotesControllerList,
  claimInternalNotesControllerListQueryKey,
  internalNotesControllerList,
  internalNotesControllerListQueryKey,
  useClaimInternalNotesControllerCreate,
  useClaimInternalNotesControllerDelete,
  useClaimInternalNotesControllerUpdate,
  useInternalNotesControllerCreate,
  useInternalNotesControllerDelete,
  useInternalNotesControllerUpdate,
} from 'korint-sdk';

export type InternalNotesOwner =
  | { type: 'policy'; policyId: string }
  | { type: 'claim'; claimId: string };

export type InternalNote = Omit<InternalNoteDto, 'policyId'>;

type InternalNotesPage = Omit<ListInternalNotesOutput, 'data'> & { data: InternalNote[] };

type InternalNotesApi = {
  queryKey: readonly unknown[];
  list: (page: number, count: number) => Promise<InternalNotesPage>;
  create: (content: string) => Promise<unknown>;
  update: (noteId: string, content: string) => Promise<unknown>;
  remove: (noteId: string) => Promise<unknown>;
  isCreating: boolean;
};

export function useInternalNotesApi(owner: InternalNotesOwner): InternalNotesApi {
  const createPolicyNote = useInternalNotesControllerCreate();
  const updatePolicyNote = useInternalNotesControllerUpdate();
  const deletePolicyNote = useInternalNotesControllerDelete();
  const createClaimNote = useClaimInternalNotesControllerCreate();
  const updateClaimNote = useClaimInternalNotesControllerUpdate();
  const deleteClaimNote = useClaimInternalNotesControllerDelete();

  if (owner.type === 'claim') {
    const { claimId } = owner;
    return {
      queryKey: claimInternalNotesControllerListQueryKey(claimId),
      list: (page: number, count: number) =>
        claimInternalNotesControllerList(claimId, { count, page }),
      create: (content: string) => createClaimNote.mutateAsync({ claimId, data: { content } }),
      update: (noteId: string, content: string) =>
        updateClaimNote.mutateAsync({ claimId, noteId, data: { content } }),
      remove: (noteId: string) =>
        deleteClaimNote.mutateAsync({ claimId, noteId, params: { hard: 'false' } }),
      isCreating: createClaimNote.isPending,
    };
  }

  const { policyId } = owner;
  return {
    queryKey: internalNotesControllerListQueryKey(policyId),
    list: (page: number, count: number) => internalNotesControllerList(policyId, { count, page }),
    create: (content: string) => createPolicyNote.mutateAsync({ policyId, data: { content } }),
    update: (noteId: string, content: string) =>
      updatePolicyNote.mutateAsync({ policyId, noteId, data: { content } }),
    remove: (noteId: string) =>
      deletePolicyNote.mutateAsync({ policyId, noteId, params: { hard: 'false' } }),
    isCreating: createPolicyNote.isPending,
  };
}
